import {
  AfterViewInit,
  Component,
  HostListener,
  inject,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { IonInput, ModalController } from '@ionic/angular';
import {
  AnimalSpecies,
  animalSpeciesFromRvoCode,
  animalSpeciesSelectOptions,
} from '@tcc-mono/vandijk/shared/data-access/models';
import { take } from 'rxjs/operators';
import { parseAnimalIdentification } from './animal-info.utils';
import { AnimalLookupDialogService } from './animal-lookup-dialog.service';

@Component({
  selector: 'tcc-mono-animal-lookup-dialog',
  templateUrl: './animal-lookup-dialog.component.html',
  styleUrls: ['./animal-lookup-dialog.component.scss']
})
export class AnimalLookupDialogComponent implements OnInit, AfterViewInit {
  private readonly _modalCtrl = inject(ModalController);
  private readonly _animalLookupDialog = inject(AnimalLookupDialogService);

  @ViewChild('scanInput') public scanFocus: IonInput;

  /** Prefill bij "Opnieuw zoeken". */
  @Input() public initialCountry: string | null = null;
  @Input() public initialId: string | null = null;
  @Input() public initialSpecies: string | null = null;

  public speciesCtrl = new FormControl(AnimalSpecies.Rund, Validators.required);
  public countryCtrl = new FormControl('NL', Validators.required);
  public idCtrl = new FormControl('', Validators.required);
  public error: string | null = null;
  public readonly speciesOptions = animalSpeciesSelectOptions();

  private _scannedNumber = '';
  private _startListening = (event: KeyboardEvent): void => {
    if (!isNaN(parseInt(event.key, 10))) {
      this._scannedNumber += event.key;
    }
  };

  @HostListener('window:keydown', ['$event'])
  public handleKeyboardStart(event: KeyboardEvent): void {
    if (event.key === '{') {
      this._scannedNumber = '';
      document.addEventListener('keydown', this._startListening);
    }

    if (event.key === '}') {
      document.removeEventListener('keydown', this._startListening);

      if (this._scannedNumber) {
        this.idCtrl.setValue(this._scannedNumber);
        this.submit();
      }
    }
  }

  public ngOnInit(): void {
    const species = this.initialSpecies
      ? animalSpeciesFromRvoCode(String(this.initialSpecies))
      : null;
    if (species) {
      this.speciesCtrl.setValue(species);
    }
    if (this.initialCountry?.trim()) {
      this.countryCtrl.setValue(this.initialCountry.trim().toUpperCase());
    }
    if (this.initialId?.trim()) {
      this.idCtrl.setValue(this.initialId.trim());
    }
  }

  public ngAfterViewInit(): void {
    setTimeout(() => this._focusScanInput(), 150);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.submit();
    }
  }

  public submit(): void {
    if (this.speciesCtrl.invalid || this.countryCtrl.invalid || this.idCtrl.invalid) {
      return;
    }

    this.error = null;
    const { country_code, identification_number } = parseAnimalIdentification(
      this.countryCtrl.value,
      this.idCtrl.value
    );

    if (!identification_number) {
      this.error = 'Levensnummer is verplicht.';
      return;
    }

    void this._modalCtrl.dismiss().then(() => {
      this._animalLookupDialog
        .openInfo({
          country_code,
          identification_number,
          animal_species: this.speciesCtrl.value ?? AnimalSpecies.Rund,
        })
        .pipe(take(1))
        .subscribe();
    });
  }

  public close(): void {
    void this._modalCtrl.dismiss();
  }

  private _focusScanInput(): void {
    void this.scanFocus?.setFocus();
  }
}
